<template>
  <div>
    <!-- Vue principale de l'exposition -->
    <div v-if="!homePage" class="exhibition-3d-gallery">
      <!-- En-tête animé -->
      <header class="gallery-header">
        <h1 class="gallery-title">{{ name }}</h1>
      </header>

      <!-- Salle virtuelle en 3D avec trois murs -->
      <div class="gallery-room">
        <!-- Mur de gauche -->
        <div class="wall left-wall">
          <div
            v-for="resource in resources.filter((r, i) => i % 3 === 0)"
            :key="resource.id"
            class="resource-card"
          >
            <div class="media-wrapper">
              <img
                v-if="isImage(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                alt="Resource Image"
                class="media"
              />
              <video
                v-else-if="isVideo(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                controls
                class="media"
              ></video>
            </div>
            <div class="card-info">
              <h2 class="card-title">{{ resource.title }}</h2>
              <p class="card-description">{{ resource.description }}</p>
            </div>
          </div>
        </div>

        <!-- Mur central -->
        <div class="wall center-wall">
          <div
            v-for="resource in resources.filter((r, i) => i % 3 === 1)"
            :key="resource.id"
            class="resource-card"
          >
            <div class="media-wrapper">
              <img
                v-if="isImage(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                alt="Resource Image"
                class="media"
              />
              <video
                v-else-if="isVideo(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                controls
                class="media"
              ></video>
            </div>
            <div class="card-info">
              <h2 class="card-title">{{ resource.title }}</h2>
              <p class="card-description">{{ resource.description == 'undefined' ? '' : resource.description }}</p>
            </div>
          </div>
        </div>

        <!-- Mur de droite -->
        <div class="wall right-wall">
          <div
            v-for="resource in resources.filter((r, i) => i % 3 === 2)"
            :key="resource.id"
            class="resource-card"
          >
            <div class="media-wrapper">
              <img
                v-if="isImage(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                alt="Resource Image"
                class="media"
              />
              <video
                v-else-if="isVideo(resource.name)"
                :src="resourceUrls[resources.indexOf(resource)]"
                controls
                class="media"
              ></video>
            </div>
            <div class="card-info">
              <h2 class="card-title">{{ resource.title }}</h2>
              <p class="card-description">{{ resource.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Pied de page animé -->
      <footer class="gallery-footer">
        <span>2021 ALPA TECHNOLOGIES</span>
        <span>Mentions Légales</span>
        <span>Fattu in Corsica</span>
      </footer>
    </div>

    <!-- Vue d'accueil -->
    <div v-else class="home-section">
      <component :is="selectedTemplate" :homeImage="homeImage" @exit="exitHomePage"></component>
    </div>
  </div>
</template>

<script>

/* eslint-disable no-unused-vars */
import { ref, onMounted } from "vue";
import axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import Modele_2 from "../../components/Models/Modele_2.vue";
import Modele_3 from "../../components/Models/Modele_3.vue";
import Modele_1 from "../../components/Models/Modele_1.vue";
import Modele_4 from "../../components/Models/Modele_4.vue";
import Modele_5 from "../../components/Models/Modele_5.vue";
import Modele_6 from "../../components/Models/Modele_6.vue";
import Modele_7 from "../../components/Models/Modele_7.vue";
import { useRoute } from "vue-router";
gsap.registerPlugin(ScrollTrigger);

export default {
  layout: "default",
  components: {
    Modele_2,
    Modele_3,
    Modele_1,
    Modele_4,
    Modele_5,
    Modele_6,
    Modele_7,
  },
  props: {
    exhibitId: {
      type: [String, Number],
      default: null
    },
    homePageProps: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const route = useRoute();
    const id = ref( props.exhibitId || route.params.id);
    const resources = ref([]);
    const name = ref("");
    const userName = ref("");
    const headImage = ref([]);
    const selectedTemplate = ref("");
    const homePage = ref(props.homePageProps == false && props.exhibitId ? false : true);
    const homeImage = ref({});
    const resourceUrls = ref([]);

    // Variables inutilisées dans ce template (conservées dans le script)
    const isImage = (fileName) => {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
    };
    const isVideo = (fileName) => {
      return /\.(mp4|mov|avi)$/i.test(fileName);
    };
    const leftWallResources = [];
    const centerWallResources = [];
    const rightWallResources = [];
    const roomStyle = {};
    const moveToWall = () => {};

    const getHeadImage = (index) => {
      let url = `${process.env.VUE_APP_BASEURL}/s3Resource/${resources.value[index].name}`;
      return url;
    };

    const fetchResources = async () => {
      if (resources.value.length === 0) {
        await getResources();
      }
      const urls = resources.value.map(
        (resource) =>
          `${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`
      );
      try {
        const responses = await Promise.all(urls.map((url) => axios.get(url)));
        resourceUrls.value = responses.map((response) => response.data);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des URLs des ressources",
          error
        );
      }
    };

    const getUserName = async () => {
      let url = `${process.env.VUE_APP_BASEURL}/user/${id.value}`;
      let response = await axios.get(url);
      userName.value = response.data.name;
    };

    const getHomeImage = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${id.value}`;
        let response = await axios.get(url);
        homeImage.value = response.data[0];
      } catch (error) {
        console.error(error);
      }
    };

    const getModel = async () => {
      try {
        let url = `${process.env.VUE_APP_BASEURL}/model/${id.value}`;
        let response = await axios.get(url);
        if (response.data.length > 0) {
          selectedTemplate.value = response.data[0].model;
          selectedTemplate.value = selectedTemplate.value
            .replace(/è/g, "e")
            .replace(/\s+/g, "_");
        }
      } catch (error) {
        console.error(error);
      }
    };

    const exitHomePage = () => {
      homePage.value = false;
      gsap.fromTo(
        ".exhibition-3d-gallery",
        { opacity: 0 },
        { opacity: 1, duration: 1, ease: "power2.inOut" }
      );
    };

    const getExhibitName = async () => {
      let url = `${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`;
      let response = await axios.get(url);
      name.value = response.data.exhibit_name;
    };

    const getResources = async () => {
      let exhibitId = id.value;
      try {
        let resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
        let response = await axios.post(resourcesUrl, { exhibitId });
        let allResources = response.data;
        let galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${exhibitId}`;
        let galleryResponse = await axios.get(galleryUrl);
        let galleryResourceIds = galleryResponse.data.map((res) => res.resource_id);
        resources.value = allResources.filter((resource) =>
          galleryResourceIds.includes(resource.id)
        );
      } catch (error) {
        console.error("Error fetching resources:", error);
      }
    };

    onMounted(() => {
      getHomeImage();
      getResources();
      fetchResources();
      getExhibitName();
      getUserName();
      getModel();

      // Animation GSAP pour l'en-tête
      gsap.from(".gallery-header", {
        y: -100,
        opacity: 0,
        duration: 1,
        ease: "bounce.out",
      });
      // Animation des murs de la salle
      gsap.from(".wall", {
        opacity: 0,
        y: 50,
        duration: 1,
        stagger: 0.3,
        ease: "power2.out",
      });
    });

    return {
      resources,
      name,
      userName,
      headImage,
      selectedTemplate,
      homePage,
      homeImage,
      resourceUrls,
      getHeadImage,
      exitHomePage,
      isImage,
      isVideo,
    };
  },
};
</script>

<style scoped>
/* Style global de la galerie 3D */
.exhibition-3d-gallery {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  background: radial-gradient(ellipse at center, #1e1e1e, #000);
  color: #f0f0f0;
}

/* En-tête de la galerie */
.gallery-header {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 20px;
}
.gallery-title {
  font-size: 3rem;
  text-transform: uppercase;
  letter-spacing: 4px;
  margin: 0;
}

/* Salle virtuelle en 3D */
.gallery-room {
  position: relative;
  perspective: 1500px;
  height: calc(100vh - 160px);
  margin: 20px;
}

/* Chaque mur */
.wall {
  position: absolute;
  top: 50%;
  transform-style: preserve-3d;
  width: 28%;
  max-height: 80%;
  overflow-y: auto;
  background: rgba(20, 20, 20, 0.8);
  border: 1px solid #333;
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 8px 16px rgba(0,0,0,0.4);
}

/* Positionnement et rotation des murs */
.left-wall {
  left: 0;
  transform: translateY(-50%) rotateY(45deg);
}
.center-wall {
  left: 36%;
  transform: translateY(-50%) rotateY(0deg);
}
.right-wall {
  right: 0;
  transform: translateY(-50%) rotateY(-45deg);
}

/* Carte ressource 3D */
.resource-card {
  background: #222;
  margin: 10px 0;
  padding: 10px;
  border-radius: 8px;
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
}
.resource-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 24px rgba(0,0,0,0.5);
}

/* Zone média */
.media-wrapper {
  width: 100%;
  height: 150px;
  overflow: hidden;
  margin-bottom: 10px;
}
.media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Informations de la carte */
.card-info {
  color: #fff;
}
.card-title {
  font-size: 1.2rem;
  margin: 0;
}
.card-description {
  font-size: 0.9rem;
  color: #aaa;
}

/* Pied de page */
.gallery-footer {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 10px;
  font-size: 0.9rem;
  color: #777;
}
  
/* Vue d'accueil */
.home-section {
  height: 100vh;
  background: #000;
}
</style>
